import type { NavigationType } from 'react-router-dom';

import { mixpanelClient } from './mixpanel';
import { getRecipientIdsWithAmounts } from './donate';

import type { CandidateType } from 'components/Slate/Candidates/Candidates';
import type { IAllocationType, ProcessingRedirect } from 'store/donatePage/types';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';
import type { PaymentMethod, PaymentMethodType } from 'store/pay/types';
import type { DonorSearchSource, RecipientsRequestType } from 'store/recipients/types';

/* -------------------------------------------------------------------------- */
/* Shared property types                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Properties shared by the Stripe checkout funnel events. Declared once because Mixpanel's
 * Lexicon describes properties globally (one description per property name, project-wide).
 */
type StripeCheckoutBaseProps = {
  payment_processor: PaymentProcessor;
  is_recurring: boolean;
  client_id?: number;
  amount: number;
  is_stripe: boolean;
  is_express: boolean;
};

/** Properties shared by the DE/DAF checkout funnel events. Built by `getDECheckoutBaseProps`. */
type DECheckoutFunnelProps = {
  payment_processor: PaymentProcessor;
  user_id: string;
  amount: number;
  recipient_ids: string;
  is_stripe: boolean;
};

/** Context the DE/DAF funnel helpers derive their properties from. */
type DECheckoutEventContext = {
  /**
   * Declared by the caller, not hardcoded here: this helper serves DE and DAF
   */
  paymentProcessor: 'DE' | 'DAF';
  userId: string;
  candidates: CandidateType[];
  donationAmount: number;
};

/**
 * The rail a donation is processed through. One of the two payment axes — `PaymentInstrument`
 * is the other, describing what the donor paid with.
 *
 * Diverges from `is_stripe` which describes the current checkout flow.
 * It's set from `processing_redirect`, so it's already true on `Donate Page Viewed`
 * before any payment exists and doesn't mean "Stripe processed this" — a Stripe-hosted page
 * taking a wire pledge is `is_stripe: true` **and** `payment_processor: 'Offline'` at once,
 * which the boolean alone cannot express. Don't "fix" one to match the other.
 *
 * They agree where the processor is `'Stripe'` or `'DE'`, which is what makes the two reconcilable
 * in live data until `is_stripe` retires alongside DE.
 */
export type PaymentProcessor =
  /** Stripe-hosted checkout. Pages configured with `processing_redirect === 'STRIPE'`. */
  | 'Stripe'
  /** Democracy Engine, the legacy rail. Being sunset — see the `DE*` prefixed helpers. */
  | 'DE'
  /** Donor-advised fund via Chariot. A donor choice at click time, not page configuration. */
  | 'DAF'
  /**
   * Engage Labs. No production pages use this today. Kept because `EN_GENERAL` is still a live
   * `ProcessingRedirect` member routing to `engageLabsCheckoutRedirect` (`Main/index.tsx`).
   */
  | 'EngageLabs'
  /** No processor: the donor pledged to send a wire, grant or check directly. */
  | 'Offline';

/**
 * The instrument the donor paid with — the second payment axis; `PaymentProcessor` is how the
 * money moves.
 *
 * Derived from `PaymentMethod` in `store/pay/types.ts` to keep it in sync. `'stripe'` is
 * excluded because it's a processor — covered by `PaymentProcessor`. `'pledge'` is excluded
 * because it's a placeholder that resolves to one of the offline instruments defined by
 * `PaymentMethodType` (`wire | grant | check`), which combines with the Stripe pair
 * (`credit_card | ach`) to make up the full set.
 */
export type PaymentInstrument = Exclude<PaymentMethod, 'stripe' | 'pledge'> | PaymentMethodType;

export type SupportOathCTAPlacement = 'footer' | 'impact_score' | 'profile';

export type ImpactReportShareMethod = 'share_sheet' | 'download' | 'link_only';

export type ImpactSlideName = 'intro' | 'donations' | 'races' | 'issues' | 'summary' | 'no_report';

/** Shared by the donor-search events. */
export type DonorSearchTrackingParams = {
  search_source: DonorSearchSource;
  query: string;
  result_count: number;
  client_id?: number;
};

/**
 * The search request args, spelled out rather than derived from `getShoppingCartProps`, so the
 * catalog lists every property Mixpanel receives. Adding a search filter must be a deliberate
 * choice here — `mixpanelEvents.test.ts` fails the build if this drifts from the source type.
 */
export type ShoppingCartSearchArgs = {
  client_id: number;
  q?: string;
  slates?: string[];
  states?: string[];
  types?: string[];
  recipient_filter_categories?: string[];
  federal_offices?: string[];
  state_offices?: string[];
  local_offices?: string[];
  organization_types?: string[];
  includeDefaults?: boolean;
  request_type?: RecipientsRequestType;
  request_location_path: string;
  page_query_str?: string | null;
  client_query_str?: string | null;
};

/** Shopping-cart search response payload: the request args plus the result summary. */
export type ShoppingCartSearchResultProps = ShoppingCartSearchArgs & {
  timestamp: string;
  result_count: number;
  query_was_broadened?: boolean;
  recipients: {
    recipient_id: string;
    recipient_name?: string;
    recipient_type: string;
    /** `null` when the search returned no score for the recipient. */
    match_score: number | null;
  }[];
};

/** Shared by the three leave-cart modal events. */
export type LeaveCartModalProps = {
  client_id: number;
  page_query_string: string;
  recipient_count: number;
  /** Pathname of the blocked navigation's target. */
  destination_path: string;
  /** `POP` is Back/Forward; `PUSH`/`REPLACE` are in-app links. */
  history_action?: NavigationType;
};

export type DonorCartProps = {
  cart_id: string;
  client_id: number;
  /** Set explicitly: the ambient `query_string` comes from `p=`, which /cart doesn't have. */
  page_query_string: string;
  recipient_ids: string[];
  recipient_count: number;
};

/**
 * The rail a donate-page click will use from the donor's chosen method plus the page's configured
 * processor. Only meaningful once the donor has chosen, which is why the two donate-page view
 * events don't carry `payment_processor` at all and keep `is_stripe` instead.
 *
 * Offline donate buttons pass `'pledge'`, and every other donate button pass `undefined`.
 * The parameter stays `string` because the whole `onDonateClick` chain is typed that way across
 * five components, and `Main` uses the value for routing as well as tracking. Narrowing it here
 * alone would just push a cast upstream.
 *
 * Takes the page's `processing_redirect` rather than a pre-collapsed `isStripePage` boolean so
 * that a new `ProcessingRedirectOptions` member is a compile error here instead of a silent
 * misattribution to `'DE'`.
 *
 * `'DAF'` never flows through here. `onDafDonateClick` is a separate handler with no method
 * choice to make, so it sends `payment_processor: 'DAF'` as a literal at its own call site.
 */
export const getDonateClickProcessor = (
  selectedMethod: string | undefined,
  processingRedirect: ProcessingRedirect | undefined
): PaymentProcessor => {
  if (selectedMethod === 'pledge') return 'Offline';
  switch (processingRedirect) {
    case ProcessingRedirectOptions.STRIPE:
      return 'Stripe';
    case ProcessingRedirectOptions.EN_GENERAL:
      return 'EngageLabs';
    default:
      return 'DE';
  }
};

/* -------------------------------------------------------------------------- */
/* The event catalog                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Central typed catalog of Mixpanel events: the key is the Title Case event name Mixpanel
 * receives, the value is that event's snake_case property shape. Every event we emit belongs
 * here — `mixpanelClient.track` is generic over this map, so an event that isn't declared, or
 * one whose properties don't match its declaration, won't compile. There is no untyped path for
 * *events* — but super properties are a separate channel (`mixpanelClient.register`), so they
 * reach Mixpanel without appearing here.
 *
 * A `type` rather than an `interface` on purpose: an exported interface can be reopened from
 * any other file via declaration merging, which would let a component quietly add its own
 * event and defeat the single-source-of-truth this file exists to provide. A type alias can't
 * be merged.
 *
 * On every event but absent from these entries: `mixpanelClient.track` injects `session_id`,
 * `ref_code`, `query_string` and `is_authenticated`.
 */
export type MixpanelEventMap = {
  /* ---- Donate page (Main) ---- */

  /**
   * Fires only on a successful page load — hits on broken or missing partner pages emit
   * nothing and can re-fire when the user switches pages in-app.
   */
  'Donate Page Viewed': {
    client_id?: number;
    is_stripe: boolean;
    allocation_type: IAllocationType;
    tags: string[];
  };
  'Donation Amount Changed': {
    is_recurring: boolean;
    client_id?: number;
    /** Absent when the donor clears the total — the input's change handler takes `number | undefined`. */
    amount?: number;
    is_stripe: boolean;
  };
  'Recipient Selection Changed': {
    recipient_id: string;
    /** false when the recipient was de-selected. */
    is_selected: boolean;
  };
  'Donate Button Clicked': {
    /**
     * The rail this click will use. `'DAF'` comes from `onDafDonateClick` as a literal. All
     * other values come from `getDonateClickProcessor`.
     */
    payment_processor: PaymentProcessor;
    client_id?: number;
    is_recurring: boolean;
    is_stripe: boolean;
    allocation_type: IAllocationType;
    /**
     * `null` when the split wasn't evaluated on this click. Note `null` is transmitted to
     * Mixpanel, unlike an absent/undefined property.
     */
    used_default_split: boolean | null;
  };
  /**
   * Fires after donation validation passes, immediately before navigating to checkout — a
   * distinct funnel step from `Donate Button Clicked`, which fires on the click itself.
   */
  'Donation Started': {
    amount: number;
    recipient_ids: string;
  };
  'Donate Page Split Amount Edited': { allocation_type: IAllocationType };

  /* ---- Shared components ---- */

  /**
   * One event across all `SupportOathCTA` placements — `placement` is what separates them,
   * so per-placement conversion is a breakdown rather than three event names.
   */
  'Support Oath CTA Clicked': { placement: SupportOathCTAPlacement };
  'Recipient Contribution Summary Viewed': { recipient_id: string };
  'Top Issue Donate Button Clicked': {
    tag_id: number;
    /**
     * The current URL's `?p=`, additionally reported as a set. Ambient `query_string` already
     * carries the same value — this is the entity claim, not a second value. Both are deliberate:
     * `TopIssueCard` renders in ~6 contexts, so `query_string` is the honest raw value and this
     * says when it resolves against the sets table rather than the pages table.
     */
    set_query_str?: string;
  };
  /** Fires only on expand, not on collapse. */
  'FAQ Expanded': { faq_id: number; question: string };
  'Header Nav Clicked': { destination: string };
  'Alert Bell Clicked': { user_id?: string };

  /* ---- Landing page ---- */
  'Landing Page Recipient Spotlight Viewed': { link_url: string };
  'Landing Page Election Countdown Viewed': { link_url: string };
  'Landing Page Recipient Spotlight Donate Link Clicked': { link_url: string };
  'Landing Page Election Countdown Donate Link Clicked': { link_url: string };

  /* ---- Autopilot / Monthly ---- */

  'Autopilot Page Viewed': Record<string, never>;
  'Autopilot Donate Button Clicked': {
    amount?: number;
    tag_id?: number;
  };
  /** Never emitted in practice: `MonthlyScreen` is unreachable (`/monthly` redirects to `/autopilot`). */
  'Monthly Page Viewed': Record<string, never>;
  /** Never emitted in practice — see `Monthly Page Viewed`. */
  'Monthly Donate Button Clicked': {
    amount: number;
    tag_id?: number;
    /**
     * Overrides ambient `ref_code`: this is the user's own fundraiser code, not the referral.
     * Required-but-nullable, not optional — the call site must write the key, because writing it
     * is what overwrites the ambient value away. An optional key can be omitted, which would let
     * the URL/storage referral silently through under this name. (`Donate Again Button Clicked`
     * declares its override as a plain `string` for the same reason; the difference is only that
     * its source value can't be undefined and this one's can.)
     */
    ref_code: string | undefined;
  };

  /* ---- Set page ---- */

  'Set Page Viewed': {
    /** See `Top Issue Donate Button Clicked` — reported alongside ambient `query_string`. */
    set_query_str?: string;
  };

  /* ---- Profile ---- */

  'Profile Page Viewed': { user_id?: string };
  'Profile Donate Button Clicked': {
    user_id: string;
    /** A query string here, not a numeric id — unlike `tag_id` on the donate-button events. */
    tag_id: string;
    /** The suggested amount, not an amount the donor chose. */
    recommended_amount: number;
  };
  'Profile View Personal Impact Report Button Clicked': null;
  /** Fires once when the impact card is rendered with content, not while it's pending. */
  'Profile Impact Report Section Viewed': {
    has_personal_report: boolean;
  };
  /** Re-fires if the user taps back then forward; count distinct users per slide. */
  'Profile Impact Report Slide Viewed': {
    slide: ImpactSlideName;
  };
  /** Fires once when the user reaches the final summary slide. */
  'Profile Impact Report Completed': null;
  /** Fires once the share completes; the share sheet doesn't report which app was chosen. */
  'Profile Impact Report Shared': {
    method: ImpactReportShareMethod;
    year: number;
  };
  'Profile With Dot Enabled Viewed': {
    conversation_id: string;
  };
  'Profile Dot Section Viewed': {
    conversation_id: string;
  };
  'Profile Dot Response Donate Link Clicked': {
    link_url: string;
    response_text: string;
    raw_question: string;
    conversation_id: string;
  };
  /** A link auto-detected inline in Dot's answer text, as opposed to one of
   * the structured donation_links CTAs (tracked by the event above). */
  'Profile Dot Response Inline Link Clicked': {
    link_url: string;
    response_text: string;
    raw_question: string;
    conversation_id: string;
  };
  'Profile Tag Selection Changed': { user_id: string; tag_id: number; is_selected: boolean };
  /** The call site passes a raw string, not the `IEmailCadence` union it renders from. */
  'Email Cadence Changed': { user_id: string; email_cadence: string };
  'This Cycle Donate Button Clicked': { recipient_id: string };
  'This Cycle Tab Clicked': Record<string, never>;

  /* ---- Donor / shopping-cart search ---- */

  'Donor Search': DonorSearchTrackingParams & { zero_results: boolean };
  /** Dead: nothing fires this. Kept for review — delete or wire up. */
  'Recipient Selected': DonorSearchTrackingParams;
  'Shopping Cart Search Bar Submit': {
    timestamp: string;
    search_value: string;
    client_id: number;
  };
  'Shopping Cart Search Results Loaded': ShoppingCartSearchResultProps & {
    is_initial_load: boolean;
  };
  /** Fires instead of `Shopping Cart Search Results Loaded` on a Back/Forward navigation. */
  'Shopping Cart Search Revisited': ShoppingCartSearchResultProps;
  /**
   * In-app navigation off a non-empty cart was blocked. Distinct from the donate page's
   * `beforeunload` prompt, which is the browser's own dialog and fires no event.
   */
  'Shopping Cart Leave Modal Viewed': LeaveCartModalProps;
  'Shopping Cart Leave Modal Leave Clicked': LeaveCartModalProps;
  /** Also fires on the close button, Esc or an overlay click, which dismiss the modal the same way. */
  'Shopping Cart Leave Modal Stay Clicked': LeaveCartModalProps;

  /* ---- Saved donor carts (behind the `saved_donor_carts` gate) ---- */

  /** Fires on each successful save, including the first POST. */
  'Donor Cart Updated': DonorCartProps;
  /** Restore returned a cart with at least one recipient, even if all were then dropped. */
  'Donor Cart Restored': DonorCartProps & {
    /** Saved recipients `/recipients` no longer returns (e.g. now ineligible). */
    dropped_recipient_count: number;
    /** From restore start to the restored selection, excluding the Statsig and Auth0 waits. */
    restore_duration_ms: number;
  };
  /** A guest `?donor_cart` link didn't restore. */
  'Donor Cart Restore Failed': {
    cart_id: string;
    client_id: number;
    /** `not_found` is a 404 (not this browser's cart); `closed` is CONVERTED or EXPIRED. */
    reason: 'not_found' | 'closed';
  };

  /* ---- Checkout: Stripe funnel ---- */

  'Checkout Tip Changed': StripeCheckoutBaseProps & { tip_amount: number };
  'Checkout Step 1 Continue Button Clicked': StripeCheckoutBaseProps;
  /** Positional name: step 1 is the *Details* step. Renaming needs analytics sign-off. */
  'Checkout Step 1 Viewed': StripeCheckoutBaseProps;
  'Checkout Review Viewed': StripeCheckoutBaseProps;
  'Checkout Complete Donation Button Clicked': {
    page_owner: string;
    is_recurring: boolean;
    client_id?: number;
    amount: number;
    is_stripe: boolean;
    is_express: boolean;
    has_tip_option: boolean;
    tip_amount: number;
    payment_method?: PaymentInstrument;
    payment_processor: PaymentProcessor;
  };

  /* ---- Checkout: DE / DAF funnel ---- */

  'Checkout Viewed': DECheckoutFunnelProps;
  'Checkout Donation Succeeded': DECheckoutFunnelProps;
  'Checkout Donation Failed': DECheckoutFunnelProps;
  'Checkout Payment Step Viewed': DECheckoutFunnelProps;

  /* ---- Confirmation ---- */

  /**
   * Fires from five call sites across three payment paths, which is why so much is optional:
   * `user_id`/`recipient_ids` come only from the DE/DAF helpers, `is_recurring`/`client_id` are
   * absent on DAF (recurrence can't be confirmed there), and `payment_method` is set only on an
   * offline pledge. `cart_id` is set when the confirmation asks the server to close a saved cart:
   * a valid `donor_cart` and a payment that hasn't failed by that point, whether or not the request
   * succeeds. Processing (ACH) and pending DE payments count even though they can fail later.
   */
  'Checkout Confirmation Viewed': {
    amount: number;
    is_stripe: boolean;
    is_recurring?: boolean;
    client_id?: number;
    payment_method?: PaymentInstrument;
    payment_processor: PaymentProcessor;
    user_id?: string;
    recipient_ids?: string;
    cart_id?: string;
  };
  'Donate Again Button Clicked': {
    user_id: string;
    amount: number;
    /** Overrides ambient `query_string` with the destination page. Required so it can't be dropped. */
    query_string: string;
  };

  /* ---- Identity ---- */

  /** Emitted by `mixpanelClient.identifyWithMergeEvent` so Mixpanel can merge identities. */
  'User Identified': { user_id: string };
};

/* -------------------------------------------------------------------------- */
/* Dispatcher                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Track an event straight off the map. Use this at the call site for events with a single call
 * site. Events with 2+ call sites get a named helper below instead, so the property shape isn't
 * duplicated across files.
 */
export function trackEvent<Name extends keyof MixpanelEventMap>(
  eventName: Name,
  properties: MixpanelEventMap[Name] | null
): void {
  mixpanelClient.track(eventName, properties);
}

/* -------------------------------------------------------------------------- */
/* Named helpers                                                              */
/*                                                                            */
/* An event gets a named helper when either:                                  */
/*   a) it fires from 2+ call sites                                           */
/*   b) its properties need derivation that shouldn't live at the call site   */
/*                                                                            */
/* trackRecipientSelected is neither — it's dead, kept visible for review.    */
/* -------------------------------------------------------------------------- */

/**
 * Standard DE/DAF checkout property set.
 */
const getDECheckoutBaseProps = ({
  userId,
  candidates,
  donationAmount,
  paymentProcessor,
}: DECheckoutEventContext): DECheckoutFunnelProps => ({
  user_id: userId,
  payment_processor: paymentProcessor,
  amount: donationAmount,
  recipient_ids: getRecipientIdsWithAmounts(candidates),
  is_stripe: false,
});

export const trackDECheckoutViewed = (context: DECheckoutEventContext): void =>
  trackEvent('Checkout Viewed', getDECheckoutBaseProps(context));

export const trackDECheckoutPaymentStepViewed = (context: DECheckoutEventContext): void =>
  trackEvent('Checkout Payment Step Viewed', getDECheckoutBaseProps(context));

export const trackDECheckoutDonationSucceeded = (context: DECheckoutEventContext): void =>
  trackEvent('Checkout Donation Succeeded', getDECheckoutBaseProps(context));

export const trackDECheckoutDonationFailed = (context: DECheckoutEventContext): void =>
  trackEvent('Checkout Donation Failed', getDECheckoutBaseProps(context));

/**
 * The DE and DAF confirmations, which derive the full funnel property set. The Stripe, shared
 * and pledge confirmations send a smaller shape and use `trackCheckoutConfirmationViewed`.
 */
export const trackDECheckoutConfirmationViewed = (
  context: DECheckoutEventContext & { is_recurring?: boolean; client_id?: number; cart_id?: string }
): void =>
  trackEvent('Checkout Confirmation Viewed', {
    ...getDECheckoutBaseProps(context),
    // Spread conditionally: DAF omits all three keys entirely today.
    ...(context.is_recurring !== undefined && { is_recurring: context.is_recurring }),
    ...(context.client_id !== undefined && { client_id: context.client_id }),
    // Here, not in getDECheckoutBaseProps: the other DE checkout events mustn't carry it.
    ...(context.cart_id && { cart_id: context.cart_id }),
  });

/**
 * The Stripe, shared and pledge confirmations, which pass their own smaller property set.
 * See `trackDECheckoutConfirmationViewed` for the DE and DAF confirmations, which derive the
 * full funnel set instead.
 */
export const trackCheckoutConfirmationViewed = (
  properties: MixpanelEventMap['Checkout Confirmation Viewed']
): void => trackEvent('Checkout Confirmation Viewed', properties);

export const trackCompleteDonationClicked = (
  properties: MixpanelEventMap['Checkout Complete Donation Button Clicked']
): void => trackEvent('Checkout Complete Donation Button Clicked', properties);

export const trackCheckoutStep1Viewed = (
  properties: MixpanelEventMap['Checkout Step 1 Viewed']
): void => trackEvent('Checkout Step 1 Viewed', properties);

export const trackCheckoutReviewViewed = (
  properties: MixpanelEventMap['Checkout Review Viewed']
): void => trackEvent('Checkout Review Viewed', properties);

export const trackDonateAgainClicked = (
  properties: MixpanelEventMap['Donate Again Button Clicked']
): void => trackEvent('Donate Again Button Clicked', properties);

/** Fires after donation validation passes, on the way to checkout. */
export const trackDonationStarted = (
  selectedDonationAmount: number,
  candidates: CandidateType[]
): void => {
  trackEvent('Donation Started', {
    amount: selectedDonationAmount,
    recipient_ids: getRecipientIdsWithAmounts(candidates),
  });
};

/** Reports donor search activity for Recipients to Mixpanel. */
export const trackDonorSearch = (params: DonorSearchTrackingParams): void => {
  trackEvent('Donor Search', {
    search_source: params.search_source,
    query: params.query,
    ...(params.client_id && { client_id: params.client_id }),
    result_count: params.result_count,
    zero_results: params.result_count === 0,
  });
};

/** Dead — nothing calls this. See the `Recipient Selected` entry in the map. */
export const trackRecipientSelected = (params: DonorSearchTrackingParams): void => {
  trackEvent('Recipient Selected', { ...params });
};
